﻿import { ApiCaller, FullfilledApiResponse } from 'apis/ApiCaller';
import type { ContentDeliverable } from 'apis/TransactionApi';

export type SKUSortField =
    | 'Name'
    | 'AdditionalIdentifier'
    | 'CreateDate'
    | 'CreatedBy'

export type PaginatedResult<T> = {
    Items: T[];
    Page: number;
    PageSize: number;
    TotalCount: number;
};

export interface SKU {
    AdditionalIdentifier: string;
    CreateDate: Date;
    CreatedBy: string;
    CreatedDateString: string;
    Description: string;
    Id: number;
    Name: string;
    Settings: SKUSettings;
}

export type SKUSettings = {
    AllowReview: boolean;
    AllowStudyGuide: boolean;
    AssociateProduct: boolean;
    AttemptsPerTest: number;
    BooksAllowed: number;
    CoursesAllowed: number;
    DefaultPartnerId: number;
    NumberOfRedemptions: number;
    TestingMode: boolean;
    TestsAllowed: number;
    TrainingMode: boolean;
}

export interface CreateAlterSKUPayload {
    AdditionalIdentifier?: string;
    Content: ContentDeliverable[];
    NumberOfRedemptions?: number;
    SKU: string;
    SkuId: number;
    UserId?: number;
}

export class SKUApi {

    private static apiCaller = new ApiCaller('SKUManagement');

    public static GetAllPaged(
        page = 1,
        pageSize = 25,
        sortField: SKUSortField | '' = '',
        sortDir = '',
        searchTerm = ''
    ): Promise<PaginatedResult<SKU>> {

        return this.apiCaller.GET<PaginatedResult<SKU>>('GetAllPaged', {
            page,
            pageSize,
            searchTerm,
            sortDir,
            sortField
        });

    }

    public static Get(sku: string, skuId: string | number): Promise<SKU> {

        return this.apiCaller.GET<SKU>('Get', { sku, skuId });

    }

    public static GetAllContent(): Promise<{ contentFlatList: ContentDeliverable[]; contentTreeView: any }> {

        return this.apiCaller.GET<{ contentFlatList: ContentDeliverable[]; contentTreeView: any }>('GetAllContent');

    }

    public static GetContentBySKU(sku: string): Promise<ContentDeliverable[]> {

        return this.apiCaller.GET<ContentDeliverable[]>('GetContentBySKU', { sku });

    }

    public static ValidateSKU(sku: string): Promise<FullfilledApiResponse> {

        return this.apiCaller.GET<FullfilledApiResponse>('ValidateSKU', { sku });

    }

    public static UpdateDefaultContentSettings(sku: string, skuId: number, settings: SKUSettings) {

        return this.apiCaller.PUT<FullfilledApiResponse>('UpdateDefaultContentSettings', { request: { Settings: settings, Sku: sku, SkuId: skuId } });

    }

    public static UpdateSingleContentSettings(sku: string, skuId: number, contentSettingsList: ContentDeliverable[]) {

        return this.apiCaller.PUT<FullfilledApiResponse>('UpdateSingleContentSettings', { contentSettingsList, sku, skuId });

    }

    public static CreateAlterSKU(payload: CreateAlterSKUPayload): Promise<FullfilledApiResponse> {

        const request = {
            AdditionalIdentifier: payload.AdditionalIdentifier ?? '',
            Content: payload.Content,
            NumberOfRedemptions: payload.NumberOfRedemptions ?? 1,
            Sku: payload.SKU,
            SkuId: payload.SkuId,
            UserId: payload.UserId ?? 0
        };

        if (payload.SkuId > 0) {

            return this.apiCaller.PUT<FullfilledApiResponse>('CreateAlterSKU', { request });

        }

        return this.apiCaller.POST<FullfilledApiResponse>('CreateAlterSKU', { request });

    }

}
